/**
 * GA4 ecommerce events that describe a change to the cart: `add_to_cart` and `remove_from_cart`.
 *
 * Every cart control (desktop cart, cart summary, mobile cart) and the product-page hand-off
 * (see global/pending-atc.ts) go through here so the item payload is built one way.
 * Quantities are the DELTA of the change, never the resulting line quantity, and `value` is the
 * delta's worth (unit price × delta), per the GA4 ecommerce spec.
 */
import {logger} from '../logger';
import {Portal} from '../util';
import {ga4_item, GA4_retarget_item, ICartLineItem} from './i';
import {IPendingAddToCart, takePendingAddToCart} from '../global/pending-atc';

declare var dataLayer: any[];

export const GA4_LOCATION_ID = 'ChIJUcHLaPbi54kRtbVMrbSH4MU';

/**
 * One cart line as a GA4 item; `quantity` defaults to the line quantity.
 * item_name is the plain product name (title2), the same string the store list, product page and
 * purchase event send; a line without one (eGift Card) falls back to its heading (title1).
 */
export function cartLineToGa4Item(x: ICartLineItem, quantity: number = x.qty): ga4_item {
    return {
        item_id: String(x.itemId),
        item_name: x.title2 || x.title1,
        item_brand: 'CHC',
        item_category: x.gaCategory,
        item_category2: x.title1,
        item_variant: x.gaVariant,
        coupon: x.gaDiscountCode,
        index: x.gaPosition,
        item_list_name: x.gaList,
        quantity,
        currency: 'USD',
        price: x.price,
        location_id: GA4_LOCATION_ID,
        product_id: x.productId,
    };
}

function toRetargetItem(item: ga4_item): GA4_retarget_item {
    return {
        id: item.item_id,
        google_business_vertical: 'retail',
        value: item.price,
        prod_type: item.item_category,
        inst: item.item_category2 ?? '',
        portal: Portal?.PortalID ?? 0,
    };
}

function lineValue(price: number, quantity: number): number {
    return Math.round(price * quantity * 100) / 100;
}

function push(dle: Record<string, any>): void {
    try {
        dataLayer.push(dle);
    } catch (err) {
        logger.error(err);
    }
}

/**
 * Resets `ecommerce` in tag manager's data model ahead of a GA4 ecommerce push. Tag manager merges
 * each push into the model and merges arrays BY INDEX, so without the reset a one-item event
 * pushed after a longer list (view_cart, view_item_list) goes out as that whole list with only
 * its first entry replaced.
 */
export function clearEcommerce(): void {
    push({ecommerce: null});
}

/** `quantity` units of `line` were added to the cart. */
export function pushAddToCart(line: ICartLineItem, quantity: number): void {
    if (!line || !(quantity > 0)) return;
    const item = cartLineToGa4Item(line, quantity);
    const rti = [toRetargetItem(item)];
    const value = lineValue(item.price, quantity);

    clearEcommerce();
    push({
        event: 'add_to_cart',
        ecommerce: {
            currency: 'USD',
            value,
            items: [item],
        },
        retarget_items: rti,
        value: Math.round(value),
        ecomm_prodid: rti.map(x => x.id),
        ecomm_pagetype: 'basket',
        ecomm_totalvalue: Math.round(value),
        ecomm_inst: rti[0].inst,
    });
    push({event: 'goalAddtoCart'});

    // ga ua (legacy trigger shape still referenced by tag manager)
    push({
        event: 'addToCart',
        ecommerce: {
            currencyCode: 'USD',
            add: {
                products: [
                    {
                        name: item.item_name,
                        id: line.productId,
                        price: item.price,
                        brand: 'CHC',
                        category: item.item_category,
                        variant: item.item_variant,
                        quantity,
                    },
                ],
            },
        },
    });
}

/** `quantity` units of `line` were removed from the cart. */
export function pushRemoveFromCart(line: ICartLineItem, quantity: number): void {
    if (!line || !(quantity > 0)) return;
    const item = cartLineToGa4Item(line, quantity);
    const value = lineValue(item.price, quantity);

    clearEcommerce();
    push({
        event: 'remove_from_cart',
        ecommerce: {
            currency: 'USD',
            value,
            items: [item],
        },
    });

    // ga ua
    push({
        event: 'removeFromCart',
        ecommerce: {
            currencyCode: 'USD',
            remove: {
                products: [
                    {
                        name: item.item_name,
                        id: line.productId,
                        price: item.price,
                        brand: 'CHC',
                        category: item.item_category,
                        variant: item.item_variant,
                        quantity,
                    },
                ],
            },
        },
    });
}

/** A line's quantity went from `previousQty` to `newQty`: report the delta as an add or a removal. */
export function pushCartQtyChange(line: ICartLineItem, previousQty: number, newQty: number): void {
    const delta = newQty - previousQty;
    if (delta > 0) {
        pushAddToCart(line, delta);
    } else if (delta < 0) {
        pushRemoveFromCart(line, -delta);
    }
}

/**
 * The cart line a pending add landed on: the line /api/atc reported (`lineId`) when the marker has one
 * (a customized add is saved under a new ProductID, so productId alone would miss it or pick an older
 * line of the base product); otherwise the newest line for that product (a re-add of an identical
 * configuration merges into the existing line, so the marker's quantity — not the line's — is the delta).
 */
export function findPendingAddLine(lines: ICartLineItem[], pending: IPendingAddToCart): ICartLineItem | undefined {
    const lineId = Number(pending.lineId);
    if (lineId > 0) {
        const exact = (lines || []).find(x => Number(x.id) === lineId);
        if (exact) return exact;
    }
    const productId = Number(pending.productId);
    const matches = (lines || []).filter(x => Number(x.productId) === productId);
    if (matches.length === 0) return undefined;
    const sameItem = pending.itemId ? matches.filter(x => String(x.itemId) === String(pending.itemId)) : [];
    const pool = sameItem.length > 0 ? sameItem : matches;
    return pool.reduce((newest, x) => (x.id > newest.id ? x : newest), pool[0]);
}

/**
 * Cart page: report every add recorded by the product page or the server (see global/pending-atc.ts)
 * against the lines now in the cart. Returns how many `add_to_cart` events were pushed.
 */
export function pushPendingAddToCart(lines: ICartLineItem[]): number {
    let pushed = 0;
    try {
        for (const pending of takePendingAddToCart()) {
            const line = findPendingAddLine(lines, pending);
            if (!line) continue;
            pushAddToCart(line, pending.qty > 0 ? pending.qty : 1);
            pushed++;
        }
    } catch (err) {
        logger.error(err);
    }
    return pushed;
}
